// Полоса индикатора уровня на странице.
//
// Один элемент заливки с `clip-path`, а не три десятка сегментов: сегменты
// нарисованы поверх зазорами из `repeating-linear-gradient`, и двадцать раз в
// секунду меняется одно свойство одного узла.

import { FLOOR_DB, formatDb, freshBallistics, position, step } from "./level.ts";
import type { Ballistics, Marker } from "./level.ts";

export interface MeterView {
  /** Новое показание от worklet'а. */
  feed(rms: number, peak: number): void;
  /** Мерить нечего: пустая шкала и прочерк, а не ноль. */
  reset(): void;
  setMarkers(markers: Marker[]): void;
}

const percent = (db: number) => `${(position(db) * 100).toFixed(2)}%`;

export function createMeter(root: HTMLElement): MeterView {
  const scale = root.querySelector(".meter-scale") as HTMLElement;
  const fill = root.querySelector(".meter-fill") as HTMLElement;
  const peak = root.querySelector(".meter-peak") as HTMLElement;
  const value = root.querySelector(".meter-value") as HTMLOutputElement;

  let state: Ballistics = freshBallistics();
  let last = 0;
  let frame = 0;
  let markerNodes: HTMLElement[] = [];

  const render = (): void => {
    frame = 0;
    fill.style.clipPath = `inset(0 ${(100 - position(state.barDb) * 100).toFixed(2)}% 0 0)`;
    peak.style.left = percent(state.peakDb);
    peak.hidden = state.peakDb <= FLOOR_DB;
    // Число идёт за полосой, а не за последним замером: иначе полоса ещё
    // горит на спаде, а подпись уже говорит «тишина».
    //
    // Линейное число рядом с децибелами не лишнее: порог энергетического VAD
    // задаётся именно в нём, и гайд ссылается на него же.
    value.textContent =
      state.barDb > FLOOR_DB
        ? `${formatDb(state.barDb)} · ${(10 ** (state.barDb / 20)).toFixed(3)}`
        : "тишина";
    root.dataset.clip = String(state.peakDb >= -1);
  };

  return {
    feed(nextRms, nextPeak) {
      const now = performance.now();
      // Первое показание после паузы не должно тянуть за собой спад за всю
      // паузу: полоса просто встаёт на место.
      const dt = last === 0 ? 0 : Math.min(0.5, (now - last) / 1000);
      last = now;
      step(state, nextRms, nextPeak, dt);
      if (frame === 0) {
        frame = requestAnimationFrame(render);
      }
    },
    reset() {
      cancelAnimationFrame(frame);
      frame = 0;
      state = freshBallistics();
      last = 0;
      fill.style.clipPath = "inset(0 100% 0 0)";
      peak.hidden = true;
      value.textContent = "—";
      root.dataset.clip = "false";
    },
    setMarkers(markers) {
      for (const node of markerNodes) {
        node.remove();
      }
      markerNodes = markers.map((marker) => {
        const node = document.createElement("span");
        // Черта за низом шкалы встала бы на её пол сплошной, как будто порог
        // там и есть. Рисуем её у края пунктиром: «где-то левее».
        const below = marker.db < FLOOR_DB;
        node.className = below ? "meter-marker meter-marker-below" : "meter-marker";
        node.style.left = percent(marker.db);
        node.title = `${marker.label}: ${formatDb(marker.db)}${below ? ", ниже шкалы" : ""}`;
        scale.append(node);
        return node;
      });
    },
  };
}
